import { useMemo } from 'react'

import { useAdminMutation, useAdminQuery } from './api'

/** Toute ressource administrable porte au moins un identifiant. */
export interface WithId {
  id: string
}

/**
 * Enveloppe de pagination du serveur.
 *
 * Toutes les listes d'administration la renvoient, y compris quand elles
 * tiennent en une page. La déballer ici plutôt que dans chaque écran évite
 * qu'un seul d'entre eux l'oublie et affiche une page blanche.
 */
export interface Page<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  pages: number
}

/**
 * Un catalogue se lit d'un coup : 200 est le maximum accepté par le serveur.
 *
 * Paginer un écran d'administration où l'on cherche « ce plat, là » serait
 * une gêne, pas un service. Si une édition dépassait 200 plats, il faudrait
 * une recherche — pas une pagination à l'aveugle, et le compteur ci-dessous
 * le dirait.
 */
const PAGE_SIZE = 200

/**
 * Une collection administrable : lister, créer, modifier, supprimer.
 *
 * Sept collections partagent exactement ces quatre gestes (plats, packs,
 * catégories, boissons, types de tables, formules, tables physiques). Les
 * écrire sept fois garantirait qu'une invalidation de cache finisse par être
 * oubliée quelque part — et un prix modifié qui n'apparaît pas est le genre
 * de bogue qu'on ne remarque qu'en salle.
 *
 * `related` liste les clés de cache que l'écriture rend périmées **ailleurs** :
 * modifier un plat change aussi la carte publique et les packs qui le
 * contiennent.
 */
export function useCollection<T extends WithId, TCreate = unknown, TUpdate = unknown>({
  key,
  path,
  eventId,
  related = [],
  query,
}: {
  key: string
  path: string
  eventId: string | undefined
  related?: string[][]
  /**
   * Filtres de la liste. Séparés de `path` à dessein : les chemins de
   * création et de modification se dérivent de `path`, et y coller une chaîne
   * de requête produirait `/orders?category=table/<id>`.
   *
   * Ils entrent aussi dans la clé du cache : deux filtres différents sont deux
   * listes différentes, et les confondre afficherait la précédente.
   */
  query?: Record<string, string | undefined>
}) {
  const search = useMemo(() => {
    const params = new URLSearchParams({ page_size: String(PAGE_SIZE) })
    for (const [name, value] of Object.entries(query ?? {})) {
      if (value) params.set(name, value)
    }
    return params.toString()
  }, [query])

  const listKey = useMemo(() => [key, eventId, search], [key, eventId, search])
  const invalidate = useMemo(() => [listKey, ...related], [listKey, related])

  const list = useAdminQuery<Page<T>>(listKey, `${path}?${search}`, eventId)

  const create = useAdminMutation<T, TCreate>('post', () => path, eventId, invalidate)

  // `variables` porte l'identifiant ET le corps : l'un construit l'URL,
  // l'autre est envoyé. Les séparer obligerait chaque appelant à jongler avec
  // deux arguments et à se tromper une fois sur deux.
  const update = useAdminMutation<T, { id: string } & TUpdate>(
    'patch',
    (variables) => `${path}/${variables.id}`,
    eventId,
    invalidate,
  )

  const remove = useAdminMutation<void, { id: string }>(
    'delete',
    (variables) => `${path}/${variables.id}`,
    eventId,
    invalidate,
  )

  // L'ordre s'envoie **entier**, jamais « déplace celui-ci d'un cran » : deux
  // personnes qui rangent la même liste en même temps produiraient sinon des
  // positions en double, et un affichage qui change à chaque rechargement.
  const reorder = useAdminMutation<{ message: string }, { ids: string[] }>(
    'post', () => `${path}/reorder`, eventId, invalidate,
  )

  return {
    items: list.data?.items ?? [],
    /** Vrai si la collection déborde de la page : à surveiller, pas à masquer. */
    truncated: (list.data?.pages ?? 1) > 1,
    total: list.data?.total ?? 0,
    isPending: list.isPending,
    create,
    update,
    remove,
    reorder,
    /** Écriture en cours, quelle qu'elle soit : sert à figer les boutons. */
    busy: create.isPending || update.isPending || remove.isPending || reorder.isPending,
    error: create.error ?? update.error ?? remove.error ?? reorder.error,
  }
}
